<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { A11y } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper/types'
import 'swiper/css'

interface Props {
  media: Media[]
}

const props = defineProps<Props>()

const activeIndex = defineModel<number>({ default: 0 })

const swiperInstance = ref<SwiperType>()

// The carousel wraps, so there is no first or last slide to stop at — the ends
// only exist for a single-slide gallery, which hides the arrows anyway.
const loop = computed(() => props.media.length > 1)

const onSwiperInit = (swiper: SwiperType) => {
  swiperInstance.value = swiper
}

// Looping duplicates slides, so `activeIndex` counts those too — `realIndex` is
// the one that points back into `media`.
const onSlideChange = (swiper: SwiperType) => {
  activeIndex.value = swiper.realIndex
}

const prev = () => swiperInstance.value?.slidePrev()
const next = () => swiperInstance.value?.slideNext()

const activeMedia = computed(() => props.media[activeIndex.value])

// On a phone the stage is a tall strip, so a portrait picture hangs from the
// top of it rather than floating in the middle — see the stylesheet.
const previewOrientation = (item: Media) => {
  const preview = mediaPreviewImage(item)
  return preview?.width && preview?.height
    ? getOrientation(preview.width, preview.height)
    : undefined
}

const activeCaption = computed(() => captionWithCredit(activeMedia.value?.caption, activeMedia.value?.credit))

// A document is only ever previewed here — its cover, or the sheet standing in
// for it — so the file itself is a click away rather than on screen.
const activeFile = computed(() => (
  activeMedia.value && !isImageMedia(activeMedia.value) && !isVideoMedia(activeMedia.value)
    ? activeMedia.value
    : undefined
))

onKeyStroke('ArrowLeft', (e) => {
  e.preventDefault()
  prev()
})

onKeyStroke('ArrowRight', (e) => {
  e.preventDefault()
  next()
})
</script>

<template>
  <div
    class="relative h-full flex flex-col"
  >
    <Swiper
      class="media-preview-carousel flex-1"
      :modules="[A11y]"
      :initial-slide="activeIndex"
      :loop="loop"
      @swiper="onSwiperInit"
      @slide-change="onSlideChange"
    >
      <SwiperSlide
        v-for="item in media"
        :key="item.id"
        class="media-preview-slide"
      >
        <div class="media-preview-stage">
          <core-image
            v-if="mediaPreviewImage(item)"
            :image="mediaPreviewImage(item)!"
            :apply-aspect-ratio-style="false"
            class="media-preview-stage-image"
            :data-orientation="previewOrientation(item)"
          />
          <!-- The caption row below already names a captioned file, so only an
               uncaptioned one carries its name on the sheet. -->
          <core-media-preview-file
            v-else
            :media="item"
            :compact="hasRichTextContent(item.caption)"
            class="media-preview-stage-file"
            :style="{ aspectRatio: `${mediaAspectRatio(item)}` }"
          />
        </div>
      </SwiperSlide>
    </Swiper>

    <div class="relative z-1 p-1 text-sm grid grid-cols-[1fr_auto] <lg:grid-cols-1 gap-1 <lg:gap-0 items-start">
      <core-rich-text
        v-if="activeCaption"
        inline
        :content="activeCaption"
        text-size-class="text-sm"
      />
      <span v-else />

      <span class="shrink-0 flex items-baseline <lg:flex-col-reverse gap-x-1 text-right <sm:text-left">
        <a
          v-if="activeFile"
          :href="activeFile.url ?? undefined"
          target="_blank"
          rel="noopener noreferrer"
          class="text-hover-accent"
        >
          {{ $t('labels.openFile', { type: mediaTypeLabel(activeFile) }) }}
        </a>
        <slot name="actions" />
        <span>{{ activeIndex + 1 }} / {{ media.length }}</span>
      </span>
    </div>

    <button
      v-if="media.length > 1"
      type="button"
      class="group z-1 absolute left-[var(--unit--vertical)] top-1/2 -translate-y-1/2 icon-box-arrow flex items-center justify-start"
      @click="prev"
    >
      <svgo-arrow class="overflow-visible icon-arrow stroke-current stroke-default fill-none text-accent group-hover:text-black icon-hover" />
    </button>

    <button
      v-if="media.length > 1"
      type="button"
      class="group z-1 absolute right-[var(--unit--vertical)] top-1/2 -translate-y-1/2 icon-box-arrow flex items-center justify-end"
      @click="next"
    >
      <!-- The half-turn goes through the standalone `rotate` property, not through
           `transform`: `icon-hover` transitions transform, and a rotation living in
           there gets animated along with the hover scale. -->
      <svgo-arrow class="overflow-visible icon-arrow [rotate:180deg] stroke-current stroke-default fill-none text-accent group-hover:text-black icon-hover" />
    </button>
  </div>
</template>

<style scoped>
/* swiper.css centres .swiper with `margin-inline: auto`, and auto margins on
   the cross axis cancel the stretch this gets as a flex item — so it falls back
   to fit-content. Everything inside is a percentage or absolutely positioned,
   which means fit-content is 0: Swiper then measures a 0 wide container and
   writes `width: 0px` onto every slide. Take the width back explicitly. */
.media-preview-carousel {
  width: 100%;
  margin-inline: 0;
}

.media-preview-slide {
  position: relative;
}

/* The preview fills the slide edge to edge; the caption row and arrows sit on top. */
.media-preview-stage {
  position: absolute;
  inset: 0;
}

.media-preview-stage-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

/* Below `sm` the stage is taller than it is wide, so a portrait picture nearly
   fills it: pinning it to the top keeps it out from under the caption row and
   reads as the page's top edge rather than a centred float. */
@media (max-width: 639.98px) {
  .media-preview-stage-image[data-orientation="portrait"] {
    object-position: top center;
  }
}

/* A sheet has no picture to fill the slide with, so it is drawn at a document's
   size in the middle of it rather than blown up to the whole screen. */
.media-preview-stage-file {
  position: absolute;
  inset: 0;
  margin: auto;
  width: auto;
  height: 60%;
  max-width: 100%;
}
</style>
